import type { Size } from '@library/types'
import { useCallback, useEffect, useRef } from 'react'

export const useResize = (
  element?: HTMLDivElement,
  anchorLeftTopElement?: HTMLElement,
  anchorRightBottomElement?: HTMLElement,
  initialSize?: Size,
  minSize?: Size,
): void => {
  const mouseOffsetX = useRef<number>(0)
  const mouseOffsetY = useRef<number>(0)
  const elementSize = useRef<DOMRect>()

  useEffect(() => {
    if (!element || !initialSize) {
      return
    }
    if (!elementSize.current) {
      const parentSize = window.document.body.getBoundingClientRect()
      element.style.left = `${(parentSize.width - initialSize.width) / 2}px`
      element.style.top = `${(parentSize.height - initialSize.height) / 2}px`
      element.style.width = `${initialSize.width}px`
      element.style.height = `${initialSize.height}px`
      updateElementSize()
    } else {
      element.style.left = `${elementSize.current.x}px`
      element.style.top = `${elementSize.current.y}px`
      element.style.width = `${elementSize.current.width}px`
      element.style.height = `${elementSize.current.height}px`
    }
  }, [element, initialSize])

  function updateElementSize() {
    if (!element) return

    const rect = element.getBoundingClientRect()
    if (rect.height > 0 && rect.width > 0) {
      elementSize.current = rect
    }
  }

  useEffect(() => {
    if (!element || !anchorLeftTopElement || !anchorRightBottomElement) return

    anchorLeftTopElement.addEventListener('mousedown', leftTopStart())
    anchorRightBottomElement.addEventListener('mousedown', rightBottomStart())
    window.addEventListener('mouseup', leftTopEnd())
    window.addEventListener('mouseup', rightBottomEnd())

    return () => {
      anchorLeftTopElement.removeEventListener('mousedown', leftTopStart())
      anchorRightBottomElement.removeEventListener('mousedown', rightBottomStart())
      window.removeEventListener('mouseup', leftTopEnd())
      window.removeEventListener('mouseup', rightBottomEnd())
    }
  }, [element, anchorLeftTopElement, anchorRightBottomElement])

  const leftTopStart = useCallback(() => start(leftTopMove), [element])
  const rightBottomStart = useCallback(() => start(rightBottomMove), [element])

  const start =
    (move: (mouse: MouseEvent) => void) =>
    ({ clientX, clientY }: MouseEvent) => {
      if (!element) return

      const { left, top } = element.getBoundingClientRect()
      mouseOffsetX.current = clientX - left
      mouseOffsetY.current = clientY - top

      window.addEventListener('mousemove', move)
    }

  const leftTopMove = useCallback(
    (mouse: MouseEvent) => {
      if (!element || !elementSize.current) return

      const {
        left: oldLeft,
        top: oldTop,
        width: oldWidth,
        height: oldHeight,
      } = element.getBoundingClientRect()
      const left = Math.max(mouse.pageX - mouseOffsetX.current, 0)
      const top = Math.max(mouse.pageY - mouseOffsetY.current, 0)
      const height = getHeight(oldHeight + oldTop - top, minSize)
      const width = getWidth(oldWidth + oldLeft - left, minSize)
      element.style.left = `${left}px`
      element.style.top = `${top}px`
      element.style.width = `${width}px`
      element.style.height = `${height}px`
    },
    [element],
  )

  const rightBottomMove = useCallback(
    (mouse: MouseEvent) => {
      if (!element || !elementSize.current) return

      const { left: oldLeft, top: oldTop } = element.getBoundingClientRect()
      const left = mouse.pageX - mouseOffsetX.current
      const top = mouse.pageY - mouseOffsetY.current
      const height = getHeight(elementSize.current.height - oldTop + top, minSize)
      const width = getWidth(elementSize.current.width + left - oldLeft, minSize)
      element.style.width = `${width}px`
      element.style.height = `${height}px`
    },
    [element],
  )

  const leftTopEnd = useCallback(() => end(leftTopMove), [element])
  const rightBottomEnd = useCallback(() => end(rightBottomMove), [element])
  const end = (move: (mouse: MouseEvent) => void) => () => {
    if (!element) return

    updateElementSize()

    window.removeEventListener('mousemove', move)
  }
}

const getHeight = (height: number, minSize?: Size) => Math.max(minSize?.height ?? -Infinity, height)

const getWidth = (width: number, minSize?: Size) => Math.max(minSize?.width ?? -Infinity, width)
